import { useCallback } from 'react'
import { antdStaticApi } from '../../providers/antd-app-provider/antd-static-api'

export interface SubmitCandidate {
    name: string
    builderValid: boolean
    conditionCount: number
}

export type SubmitRejection = 'name' | 'builder' | 'empty'

export interface SubmitCheck {
    ok: boolean
    reason?: SubmitRejection
    message?: string
}

const MAX_NAME_LENGTH = 255

/**
 * A segment is submittable only with a name, a builder that reports itself
 * valid, and at least one condition.
 *
 * This is the single gate for every submit path. A builder rendered without a
 * visible submit button is driven imperatively instead, and that path must run
 * the same three checks — otherwise it becomes a way to persist a nameless or
 * conditionless segment.
 */
export function canSubmitSegment({ name, builderValid, conditionCount }: SubmitCandidate): SubmitCheck {
    const trimmed = name.trim()
    if (!trimmed) return { ok: false, reason: 'name', message: 'Name is required' }
    if (trimmed.length > MAX_NAME_LENGTH) {
        return {
            ok: false,
            reason: 'name',
            message: `Name must be ${MAX_NAME_LENGTH} characters or fewer`,
        }
    }

    if (!builderValid) {
        return { ok: false, reason: 'builder', message: 'Please correct the highlighted conditions.' }
    }

    // A query stating no criteria cannot describe an audience. The count is the
    // builder's own, so this asks the one place that knows how many conditions
    // there are instead of re-deriving it from serialized JSON.
    if (conditionCount === 0) {
        return { ok: false, reason: 'empty', message: 'At least 1 condition is required.' }
    }

    return { ok: true }
}

/**
 * Confirms before discarding in-progress builder edits.
 *
 * `confirmLeave` runs `proceed` immediately on a pristine form and otherwise
 * defers it to the confirmation dialog, so an exit path can call it
 * unconditionally. `isDirty` is read at call time rather than captured, so a
 * caller may pass a ref-backed getter and still see the current value.
 */
export function useUnsavedChangesGuard(isDirty: () => boolean) {
    const confirmLeave = useCallback(
        (proceed: () => void) => {
            if (!isDirty()) {
                proceed()
                return
            }

            antdStaticApi.modal.confirm({
                title: 'Unsaved Changes',
                content: 'You have unsaved changes. Are you sure you want to leave?',
                okText: 'Yes, Leave',
                cancelText: 'Stay',
                onOk: proceed,
            })
        },
        [isDirty],
    )

    return { confirmLeave }
}
